/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  Icon,
  InlineMessage,
  MenuInput,
  type MenuInputOption,
} from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import { testIds } from '../../../../shared/constants';
import { VALIDATION_TYPES } from '../../../../shared/constants/global';
import { useDebounce } from '../../../../shared/hooks';
import useInvertedLocalize from '../../../../shared/hooks/useInvertedLocalize';
import {
  type DataProvider,
  type FieldElement,
  type FormValues,
  type PreviewDataProviderPayload,
  type SingleSelectField,
} from '../../../../shared/types';
import { getDataProviderPreviewPageSize } from '../../../../shared/utils/dataProviderPageSize';
import { useDataProviderTracking } from '../../../../shared/utils/troubleshooting';
import { DATA_PROVIDER } from '../../../FormBuilder/data-layer/constants';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';
import { type EntityInitialData } from '../../data-store/initialDataSlice';
import {
  useDefaultValue,
  useGetDataProvider,
  useInfiniteScroll,
} from '../../hooks';
import { useDependencyValues } from '../../hooks/useDependencyValues';
import {
  type ApiResponse,
  parseDataProviderPreviewOptionTables,
} from '../../hooks/useGetDataProvider';
import {
  determineTriggerType,
  hasDependencies,
  prepareApiTrackingData,
} from '../../utils/dataProviderTrackingUtils';
import {
  areDependenciesSatisfied,
  buildDependencies,
  getFieldPlaceholder,
  getFixedOptions,
  handleCascadingClear,
  isOnlyContextualEntityDependencies,
  isPaginationEnabled,
  resetOptionsLoadingState,
  shouldLoadOptions,
  validateOptions,
} from '../../utils/dataProviderUtils';
import { injectDefaultOptions } from '../../utils/defaultValueUtils';
import {
  applyValueMatching,
  isMeaningfulSingleSelectValue,
  resolveProcessedSingleSelectFromOptions,
} from '../../utils/valueProcessing';
import {
  loadedSnapshotMatchesCurrentSelection,
  normalizeValuesToSearchWireFromLoaded,
  shouldAttachValuesToSearch,
} from '../../utils/valuesToSearch';

import styles from './PreviewFields.module.css';

interface SingleSelectProps {
  field: SingleSelectField;
  value?: MenuInputOption | string | number | null;
  urlSlug?: string;
  customFormUid?: string;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  canvasFields?: FieldElement[];
  dataProviders?: Record<string, DataProvider>;
  dataProvider: DataProvider;
  onChange?: (selectedOption: MenuInputOption) => void;
  formValues?: FormValues;
  isPreview?: boolean;
  isModalForm?: boolean;
  initialData?: EntityInitialData | null;
  /** When true (public LiveForm), skip HTTP/SQL data provider API calls */
  isPublicForm?: boolean;
  /** Entity or credential id in edit flows — enables valuesToSearch on data-provider requests */
  entityId?: string | null;
  /** Map of fieldName → fieldType for type-aware dependency formatting (e.g. date fields). */
  fieldTypeMap?: Map<string, string>;
  /** Loaded entity field data for edit flows — blocks default auto-select when entity data exists.
   *  Passed as prop to avoid Redux dependency in NPM package consumers. */
  loadedFormElementsData?: Record<string, unknown> | null;
}

/**
 * SingleSelect component for rendering SingleSelect fields in form preview
 * Optimized for both fixed and HTTP data providers with saved data support
 */
function SingleSelect({
  field,
  value,
  urlSlug,
  customFormUid,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  canvasFields,
  dataProviders,
  dataProvider,
  onChange,
  formValues = {},
  isPreview,
  isModalForm = false,
  initialData = null,
  isPublicForm = false,
  entityId = null,
  fieldTypeMap,
  loadedFormElementsData = null,
}: SingleSelectProps): ReactElement {
  const blockDefaultAutoSelect = useMemo(
    () =>
      Boolean(
        entityId &&
          !isPublicForm &&
          (loadedFormElementsData === null ||
            Object.prototype.hasOwnProperty.call(
              loadedFormElementsData,
              field.fieldName
            ))
      ),
    [entityId, isPublicForm, loadedFormElementsData, field.fieldName]
  );

  const [options, setOptions] = useState<MenuInputOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const l = useInvertedLocalize();

  const debouncedSearchText = useDebounce(searchText, 500);

  const dependenciesSatisfiedForProvider = useMemo(
    () =>
      isOnlyContextualEntityDependencies(dataProvider) ||
      areDependenciesSatisfied(dataProvider, formValues, initialData),
    [dataProvider, formValues, initialData]
  );

  const {
    fieldName,
    label,
    placeholder,
    helperText,
    isReadOnly: staticReadOnly,
    infoTooltipText,
    validations,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;
  const isReadOnly = staticReadOnly || isReadOnlyProp || false;
  const { mutate: getDataProviderPreview } = useGetDataProvider({
    isPreview,
    customFormUid,
  });
  // Troubleshooting tracking hook
  const {
    trackValueSelected,
    trackApiTriggered,
    trackApiSuccess,
    trackApiError,
  } = useDataProviderTracking();

  // Track if this is the first load
  const isFirstLoadRef = useRef(true);
  const isInitialMount = useRef(true);
  const isLoadInFlightRef = useRef(false);
  const shouldResetSearchRef = useRef<boolean>(false); // Track when cascading requires immediate search reset
  const skipNextSearchLoadRef = useRef<boolean>(false); // Skip search effect when cascading already handled the load
  // AbortController for cancelling in-flight search requests
  const abortControllerRef = useRef<AbortController | null>(null);
  // Timestamp of when a search-triggered load started, used to ignore DS auto-resets
  const searchLoadStartedAtRef = useRef<number>(0);
  const prevDependencyValues = useRef<string>('');
  const dependenciesChangedRef = useRef<boolean>(false); // Store dependenciesChanged before updating prevDependencyValues

  const { processedValue: defaultProcessedValue } = useDefaultValue({
    field,
    value,
    isLoading,
    options,
    onChange,
    providerType: dataProvider.type,
    formValues,
    initialData,
    blockDefaultAutoSelect,
  });

  // Process the incoming value to determine what should be selected
  const processedValue = useMemo(() => {
    if (value === null) {
      return null;
    }

    if (value === undefined || value === '') {
      return defaultProcessedValue;
    }

    if (typeof value === 'object' && 'value' in value) {
      const v = value.value;

      // Cascading clear uses { label: '', value: '' }; treat as no selection so
      // finalOptions/selectedOptions do not inject a ghost checked row.
      if (v === '' || v == null) {
        return null;
      }

      return value;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return resolveProcessedSingleSelectFromOptions(value, options);
    }

    return null;
  }, [value, dataProvider, defaultProcessedValue, options]);

  const paginationEnabled = useMemo(
    () => isPaginationEnabled(dataProvider),
    [dataProvider]
  );
  // Track only dependency field values, not entire formValues
  // This prevents API calls when unrelated fields change
  const relevantDependencyValues = useDependencyValues(
    dataProvider,
    formValues,
    initialData
  );

  // Clear field value when dependencies change (cascading)
  useEffect(() => {
    const didCascade = handleCascadingClear(
      dataProvider,
      formValues,
      relevantDependencyValues,
      prevDependencyValues.current,
      value,
      onChange,
      initialData
    );

    // Reset search text and refs if cascading occurred
    if (didCascade) {
      setSearchText('');
      shouldResetSearchRef.current = true;
      skipNextSearchLoadRef.current = true;
    }
    // Only run when dependencies change, not when value or onChange changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relevantDependencyValues]);

  // Cleanup: abort any in-flight request on unmount
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  // Fallback: if loading persists beyond API timeout, show error
  useEffect(() => {
    if (!isLoading || loadError) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (isLoading && !loadError) {
        setIsLoading(false);
        setLoadError('Unable to load options');
      }
    }, 12000); // Slightly longer than API_TIMEOUT_IN_MS (10s)

    return () => clearTimeout(timer);
  }, [isLoading, loadError]);
  // Reset options isChecked when value becomes null (form cleared)
  useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setOptions((prev) => prev.map((opt) => ({ ...opt, isChecked: false })));
    }
  }, [value]);

  // Load options for HTTP and SQL data providers
  const loadMoreOptions = useCallback(
    (
      currentPage: number,
      dependenciesChangedOverride?: boolean,
      searchTextOverride?: string
    ): void => {
      if (
        dataProvider.type !== DATA_PROVIDER.HTTP &&
        dataProvider.type !== DATA_PROVIDER.SQL &&
        dataProvider.type !== DATA_PROVIDER.DATASTORE
      ) {
        return;
      }

      if (
        isPublicForm ||
        (isPreview && dataProvider.type === DATA_PROVIDER.SQL)
      ) {
        setIsLoading(false);
        setIsLoadingMore(false);

        if (currentPage === 0) {
          setOptions([]);
        }

        setHasMoreOptions(false);

        return;
      }

      if (currentPage > 0 && !hasMoreOptions) {
        return;
      }

      // Only manage AbortController for search requests (page 0)
      // Pagination requests (page > 0) don't need cancellation
      let signal: AbortSignal | undefined;

      if (currentPage === 0) {
        // Cancel previous search request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Create new AbortController for this search request
        const controller = new AbortController();

        abortControllerRef.current = controller;
        signal = controller.signal;
      }

      // Set loading states
      if (currentPage === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const dependencies = buildDependencies(
        dataProvider,
        formValues,
        initialData,
        fieldTypeMap
      );

      const loadedSnapshot = loadedFormElementsData?.[field.fieldName];
      let valuesToSearchPayload: unknown[] | undefined;

      if (entityId && !isPublicForm && loadedSnapshot !== undefined) {
        if (
          loadedSnapshotMatchesCurrentSelection(
            loadedSnapshot,
            value,
            'singleselect'
          )
        ) {
          valuesToSearchPayload =
            normalizeValuesToSearchWireFromLoaded(loadedSnapshot);
        }
      }

      const payload: PreviewDataProviderPayload = {
        urlSlug,
        formItemId: field.id,
        page: currentPage,
        pageSize: getDataProviderPreviewPageSize(dataProvider),
        dependencies,
        searchText:
          searchTextOverride !== undefined
            ? searchTextOverride
            : debouncedSearchText,
        ...(shouldAttachValuesToSearch(valuesToSearchPayload)
          ? { valuesToSearch: valuesToSearchPayload }
          : {}),
      };

      // Use override if provided, otherwise use the ref value (set before prevDependencyValues was updated)
      const dependenciesChanged =
        dependenciesChangedOverride !== undefined
          ? dependenciesChangedOverride
          : dependenciesChangedRef.current;

      const isFirstLoad = isFirstLoadRef.current;
      const dataProviderHasDependencies = hasDependencies(dataProvider);

      // Determine trigger type
      const triggerType = determineTriggerType(
        currentPage,
        dataProviderHasDependencies,
        dependenciesChanged,
        isFirstLoad
      );

      // Prepare API tracking data
      const trackingData = prepareApiTrackingData(
        field.id,
        field.fieldName,
        dataProvider,
        triggerType,
        currentPage,
        dependencies,
        canvasFields,
        {
          isReadOnly,
          isDisabled: isReadOnly,
          isRequired,
        },
        urlSlug
      );

      const apiEventId = trackApiTriggered({
        ...trackingData,
        startTime: Date.now(),
        status: 'pending',
      });

      (
        getDataProviderPreview as (
          payload: PreviewDataProviderPayload,
          callbacks?: {
            onSuccess?: (data: ApiResponse) => void;
            onError?: (error: Error) => void;
          },
          signal?: AbortSignal
        ) => Promise<void>
      )(
        payload,
        {
          onSuccess: (response: ApiResponse) => {
            isLoadInFlightRef.current = false;
            setIsLoading(false);
            setIsLoadingMore(false);
            setLoadError(null);

            if (response.error || response.errorMessage) {
              Logger.error(
                'Error loading data provider preview:',
                response.errorMessage
              );

              // Track API error
              if (apiEventId) {
                trackApiError(apiEventId, {
                  fieldId: field.id,
                  fieldName: field.fieldName,
                  error: {
                    type: 'api',
                    message: response.errorMessage || 'Unknown error',
                  },
                });
              }

              return;
            }

            // Track API success
            if (apiEventId) {
              trackApiSuccess(apiEventId, {
                fieldId: field.id,
                fieldName: field.fieldName,
                triggerType,
                apiResponse: {
                  statusCode: 200,
                  dataCount: response.data?.data?.data?.length || 0,
                  hasNext: response.data?.hasNext || false,
                  totalPages: response.data?.data?.totalPages,
                },
              });
            }

            const { baseRows, defaultRows } =
              parseDataProviderPreviewOptionTables(response);
            const newData =
              defaultRows.length > 0
                ? injectDefaultOptions(
                    baseRows,
                    defaultRows.map((o) => ({
                      value: String(o.value),
                      label: String(o.label ?? o.value),
                    })),
                    dataProvider.type
                  )
                : baseRows;
            const effectiveProcessed = processedValue ?? defaultRows[0] ?? null;
            const { hasNext } = response.data;

            if (currentPage === 0) {
              // For first page, apply value matching only if pagination is NOT enabled
              if (paginationEnabled) {
                setOptions(newData);
              } else {
                setOptions(applyValueMatching(newData, effectiveProcessed));
              }
            } else {
              // For subsequent pages, just append new data
              setOptions((prevOptions) => [...prevOptions, ...newData]);
            }

            setHasMoreOptions(hasNext);
          },
          onError: (requestError: Error) => {
            isLoadInFlightRef.current = false;
            setIsLoading(false);
            setIsLoadingMore(false);
            setLoadError('Unable to load options');
            Logger.error('Error loading data provider preview:', requestError);

            // Track API error
            if (apiEventId) {
              trackApiError(apiEventId, {
                fieldId: field.id,
                fieldName: field.fieldName,
                error: {
                  type: 'network',
                  message: requestError.message || 'Unknown error',
                },
              });
            }
          },
        },
        signal
      ).catch(() => {
        // Error is already handled by onError callback
      });
    },
    [
      dataProvider.type,
      canvasFields,
      dataProviders,
      field.id,
      getDataProviderPreview,
      paginationEnabled,
      processedValue,
      formValues,
      trackApiTriggered,
      trackApiSuccess,
      trackApiError,
      dataProvider.dependencies,
      debouncedSearchText,
      isPublicForm,
      hasMoreOptions,
      initialData,
      entityId,
      value,
      loadedFormElementsData,
      field.fieldName,
    ]
  );

  // Create unique dataTestId per field instance to prevent cross-component interference
  const uniqueDataTestId = `${testIds.FF_PREVIEW_SINGLE_SELECT_FIELD}-${field.id}`;

  const { handleExpandedChange: handleInfiniteScroll } = useInfiniteScroll({
    dataTestId: uniqueDataTestId,
    hasMoreOptions,
    isLoading,
    isLoadingMore,
    optionsCount: options.length,
    onLoadMore: () => {
      const nextPage = page + 1;

      setPage(nextPage);
      loadMoreOptions(nextPage, false);
    },
  });

  // Get fixed options and apply value matching
  const fixedOptions = useMemo(() => {
    const fixedOpts = getFixedOptions(dataProvider, processedValue);

    if (fixedOpts.length > 0) {
      setIsLoading(false);
    }

    return fixedOpts;
  }, [dataProvider, processedValue]);

  // Main effect to load options based on data provider type
  useEffect(() => {
    const loadOptions = (): void => {
      const dependenciesSatisfied = dependenciesSatisfiedForProvider;
      // Calculate dependenciesChanged BEFORE updating prevDependencyValues
      const dependenciesChanged =
        prevDependencyValues.current !== relevantDependencyValues;

      // Store in ref so loadMoreOptions can use it
      dependenciesChangedRef.current = dependenciesChanged;

      const shouldLoad = shouldLoadOptions(
        dependenciesSatisfied,
        dependenciesChanged,
        options.length
      );

      // Don't load options if dependencies are not satisfied
      if (!dependenciesSatisfied) {
        isLoadInFlightRef.current = false;
        setOptions([]);
        setIsLoading(false);

        return;
      }

      if (dependenciesChanged) {
        isLoadInFlightRef.current = false;
      }

      if (shouldLoad && !isLoadInFlightRef.current) {
        prevDependencyValues.current = relevantDependencyValues;
        isFirstLoadRef.current = false;

        try {
          resetOptionsLoadingState(
            setOptions,
            undefined,
            setIsLoading,
            setPage,
            setHasMoreOptions
          );

          if (
            dataProvider.type === DATA_PROVIDER.HTTP ||
            dataProvider.type === DATA_PROVIDER.SQL ||
            dataProvider.type === DATA_PROVIDER.DATASTORE
          ) {
            // Use empty search if we just cascaded, otherwise use debounced value
            const effectiveSearchText = shouldResetSearchRef.current
              ? ''
              : debouncedSearchText;

            shouldResetSearchRef.current = false; // Reset flag

            isLoadInFlightRef.current = true;
            loadMoreOptions(0, dependenciesChanged, effectiveSearchText);
          } else if (dataProvider.type === DATA_PROVIDER.FIXED) {
            setOptions(fixedOptions);
            setIsLoading(false);
          }
        } catch (optionsLoadErr) {
          Logger.error('Error loading options:', optionsLoadErr);
          setOptions([]);
          setIsLoading(false);
        }
      }
    };

    loadOptions();
  }, [
    dataProvider,
    dependenciesSatisfiedForProvider,
    field.id,
    relevantDependencyValues,
    fixedOptions,
    // loadMoreOptions intentionally excluded to prevent duplicate API calls
    // when search text changes. The search effect handles search-triggered loads.
  ]);

  // Trigger API call when search text changes
  useEffect(() => {
    // Skip on initial mount - main loading effect handles it
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }

    // Skip if cascading already triggered a load with empty search text
    // This prevents duplicate API calls when debouncedSearchText catches up to the reset
    if (skipNextSearchLoadRef.current && debouncedSearchText === '') {
      skipNextSearchLoadRef.current = false;

      return;
    }

    // Don't search if dependencies are not satisfied
    if (!dependenciesSatisfiedForProvider) {
      return;
    }

    if (
      dataProvider.type === DATA_PROVIDER.HTTP ||
      dataProvider.type === DATA_PROVIDER.SQL ||
      dataProvider.type === DATA_PROVIDER.DATASTORE
    ) {
      // Record when search load starts so we can ignore DS auto-resets
      // that fire when options transition during the load cycle
      if (debouncedSearchText) {
        searchLoadStartedAtRef.current = Date.now();
      }

      setPage(0);
      setHasMoreOptions(true);
      loadMoreOptions(0, false, debouncedSearchText);
    }
    // loadMoreOptions is intentionally excluded from deps to prevent triggering
    // when formValues change. We only want this effect to run when search text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchText,
    dataProvider.type,
    dependenciesSatisfiedForProvider,
  ]);

  /** Stable key so this effect does not re-run when `processedValue` is a new object reference with the same selection (avoids setOptions → options → resolve loop). */
  const processedValueApplyKey = useMemo(() => {
    if (!isMeaningfulSingleSelectValue(processedValue)) {
      return '';
    }

    return `${String(processedValue.value)}\u0000${String(processedValue.label ?? '')}`;
  }, [
    isMeaningfulSingleSelectValue(processedValue)
      ? String(processedValue.value)
      : '',
    isMeaningfulSingleSelectValue(processedValue)
      ? String(processedValue.label ?? '')
      : '',
  ]);

  // Apply value matching for HTTP and SQL providers when options are loaded
  useEffect(() => {
    if (
      options.length > 0 &&
      (dataProvider.type === DATA_PROVIDER.HTTP ||
        dataProvider.type === DATA_PROVIDER.SQL ||
        dataProvider.type === DATA_PROVIDER.DATASTORE) &&
      processedValue
    ) {
      setOptions((currentOptions) => {
        const next = applyValueMatching(currentOptions, processedValue);
        const sameLength = next.length === currentOptions.length;
        const sameChecks =
          sameLength &&
          next.every((opt, i) => {
            const prev = currentOptions[i];

            return (
              prev !== undefined &&
              String(opt.value) === String(prev.value) &&
              Boolean(opt.isChecked) === Boolean(prev.isChecked)
            );
          });

        return sameChecks ? currentOptions : next;
      });
    }
    // processedValue intentionally omitted: keyed by processedValueApplyKey to avoid
    // infinite loops when `resolveProcessedSingleSelectFromOptions` returns a new object per options snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedValueApplyKey, dataProvider.type, options.length]);

  const dependenciesNotSatisfied = !dependenciesSatisfiedForProvider;

  const placeholderText = getFieldPlaceholder(
    dataProvider,
    formValues,
    placeholder,
    initialData
  );

  // Remount MenuInput when dependency context OR stored value changes. Cascading
  // clears Formik after the parent update, but `relevantDependencyValues` does
  // not change on that second step — including the selection fingerprint forces
  // a fresh MenuInput when the child goes from a real option to cleared.
  const menuInputResetKey = useMemo(() => {
    if (!hasDependencies(dataProvider)) {
      return field.id;
    }

    const selectionKey = isMeaningfulSingleSelectValue(processedValue)
      ? String(processedValue.value)
      : '';

    return `${field.id}:${relevantDependencyValues}:${selectionKey}`;
  }, [dataProvider, field.id, relevantDependencyValues, processedValue]);

  const handleChange = useCallback(
    (selectedOption: MenuInputOption) => {
      // Block selection only during initial load (no options yet), not during
      // search reloads where existing options are still displayed.
      if ((isLoading && options.length === 0) || dependenciesNotSatisfied) {
        return;
      }

      // Track value selection
      if (
        dataProvider.dependencies &&
        Object.keys(dataProvider.dependencies).length > 0
      ) {
        const dependencies = buildDependencies(
          dataProvider,
          formValues,
          initialData,
          fieldTypeMap
        );

        trackValueSelected({
          fieldId: field.id,
          fieldName: field.fieldName,
          selectedValue: selectedOption,
          dependencies,
        });
      }

      // Update options state to reflect selection
      setOptions((currentOptions) =>
        currentOptions.map((opt) => ({
          ...opt,
          isChecked: String(opt.value) === String(selectedOption.value),
        }))
      );

      if (onChange) {
        onChange(selectedOption);
      }
    },
    [
      isLoading,
      dependenciesNotSatisfied,
      onChange,
      dataProvider,
      formValues,
      initialData,
      field.id,
      field.fieldName,
      trackValueSelected,
    ]
  );

  const finalOptions = useMemo(() => {
    if (dependenciesNotSatisfied) {
      return [];
    }

    // Show empty only for initial load (no options yet).
    // During search reload, keep existing options visible to avoid flicker.
    if (isLoading && options.length === 0) {
      return [];
    }

    // Handle existing processed value
    if (processedValue) {
      const selVal = String(processedValue.value);
      const selLbl = processedValue.label;

      const mapped = options.map((opt) => {
        const valueMatch = String(opt.value) === selVal;
        const labelMatch =
          opt.label !== undefined &&
          selLbl !== undefined &&
          opt.label === selLbl;

        return { ...opt, isChecked: valueMatch || labelMatch };
      });

      if (mapped.some((opt) => opt.isChecked)) {
        return mapped;
      }

      // MenuInput (design-system) drives the trigger label from
      // `options.find((o) => o.isChecked)`, not from `selectedOptions`. After
      // search closes, the current page often omits the selected row — inject
      // a checked row so the selection stays visible.
      return [
        {
          ...processedValue,
          label: processedValue.label ?? String(processedValue.value),
          isChecked: true,
        },
        ...mapped,
      ];
    }

    return options;
  }, [options, processedValue, isLoading, dependenciesNotSatisfied]);

  // Validate options to filter out invalid entries
  const validOptionsForRender = useMemo(
    () => validateOptions(finalOptions),
    [finalOptions]
  );

  const selectedOptions = useMemo(() => {
    if (
      processedValue &&
      typeof processedValue === 'object' &&
      'value' in processedValue
    ) {
      return [
        {
          ...processedValue,
          isChecked: true,
        },
      ];
    }

    return [];
  }, [processedValue]);

  const handleSearchChange = (searchValue: string) => {
    setSearchText(searchValue);
  };

  const handleInputReset = () => {
    if (
      searchText &&
      searchLoadStartedAtRef.current > 0 &&
      Date.now() - searchLoadStartedAtRef.current < 2000
    ) {
      return;
    }

    searchLoadStartedAtRef.current = 0;
    setSearchText('');
  };

  const handleExpandedChange = useCallback(
    (expanded: boolean) => {
      handleInfiniteScroll(expanded);

      if (!expanded && searchText) {
        searchLoadStartedAtRef.current = 0;
        setSearchText('');
      }
    },
    [handleInfiniteScroll, searchText]
  );

  return (
    <MenuInput
      key={menuInputResetKey}
      inputSize='medium'
      options={validOptionsForRender}
      selectedOptions={selectedOptions}
      label={label}
      inputLabel={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
        />
      }
      sortOnCheck
      minOptionsForSearch={0}
      placeholder={
        isLoading && options.length === 0 && !loadError
          ? l('Loading options...')
          : placeholderText
      }
      onChange={handleChange}
      isSearchDisabled
      onSearchTermChange={handleSearchChange}
      onInputReset={handleInputReset}
      setIsExpanded={handleExpandedChange}
      isRequired={isRequired}
      isCritical={!!(error || loadError)}
      isDisabled={isReadOnly}
      CriticalHelperText={
        <InlineMessage
          text={error || loadError || ''}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      hasMoreOptions={hasMoreOptions}
      // isLoading={
      //   (isLoadingMore || (isLoading && options.length === 0)) && !loadError
      // }
      // loadingText={isLoadingMore ? l('Loading options...') : undefined}
      // TODO: Pass isMobileWidth={isMobileXlargeWidth} once the DS SingleSelect component supports it
      // Modals need the overflow to not be hidden. Pages do need the overflow to be hidden.
      isPortalEnabled={isModalForm}
      // shouldMatchTriggerWidth
      className={styles.singleSelect}
      classNames={{
        dropdownClassName: styles.singleSelectDropdown,
        inputFieldClassName: cx(isReadOnly && styles.value__isReadOnly),
      }}
      dataTestId={uniqueDataTestId}
    />
  );
}

export default SingleSelect;
